// src/components/modals/AddActivityModal.tsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { getSupabase, currentUser } from "@/lib/supabase";

interface AddActivityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddActivityModal({ open, onClose, onSuccess }: AddActivityModalProps) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData);

    const user = await currentUser();
    if (!user) {
      alert("Not logged in");
      setLoading(false);
      return;
    }

    const { error } = await getSupabase().from("training_log").insert({
      user_id: user.id,
      activity_type: data.activity_type as string,
      avg_heart_rate: data.avg_heart_rate ? Number(data.avg_heart_rate) : null,
      calories_burned: data.calories_burned ? Number(data.calories_burned) : null,
      device_model: (data.device_model as string) || null,
      source: (data.source as string) || "manual",
      created_at: new Date().toISOString(),
    });

    setLoading(false);

    if (error) {
      alert("Error: " + error.message);
    } else {
      onSuccess?.();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#0f172a]">Add Activity</h2>
          <button type="button" onClick={onClose}>
            <X className="w-5 h-5 text-gray-500 hover:text-gray-600" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 block mb-1">Activity Type</label>
            <input name="activity_type" required className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a]" placeholder="Running, Cycling, etc." />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Avg Heart Rate (optional)</label>
            <input name="avg_heart_rate" type="number" className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a]" placeholder="165" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Calories Burned (optional)</label>
            <input name="calories_burned" type="number" className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a]" placeholder="600" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Device (optional)</label>
            <input name="device_model" className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a]" placeholder="Apple Watch" />
          </div>
          <div>
            <label className="text-sm text-gray-500 block mb-1">Source</label>
            <select name="source" className="w-full bg-[#f1f5f9] rounded-lg px-3 py-2 text-[#0f172a]">
              <option value="manual">Manual</option>
              <option value="garmin">Garmin</option>
              <option value="whoop">WHOOP</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 bg-gray-700 py-2 rounded-lg text-[#0f172a]">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="flex-1 bg-green-500 text-[#0f172a] py-2 rounded-lg font-medium disabled:opacity-60">
            {loading ? "Saving..." : "Add Activity"}
          </button>
        </div>
      </form>
    </div>
  );
}
